import { Component, computed, EventEmitter, input, Output, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChildOnboardingSituation,
  UnderageOnboardingLegalGuardianshipStatus,
  UnderageOnboardingLegitimizationDocument,
} from '@zero/onboarding/domain';
import {
  ButtonComponent,
  CheckboxComponent,
  IconButtonComponent,
  IconComponent,
  Item,
  NotificationComponent,
  SelectComponent,
} from '@zero/shared/ui-web-design-system';

export enum InitialCheckBoxType {
  customerIdLegalGuardian2 = 'customerIdLegalGuardian2',
  allGermanResidenceTax = 'allGermanResidenceTax',
  passport = 'passport',
}
type ChecklistState = Record<string, boolean>;

@Component({
  selector: 'child-onboarding-situation-checklist',
  templateUrl: './situation-checklist.component.html',
  styleUrls: ['./situation-checklist.component.scss'],
  imports: [TranslocoPipe, ButtonComponent, CheckboxComponent, SelectComponent, NotificationComponent, IconComponent, IconButtonComponent],
})
export class SituationChecklistComponent {
  public situations = input<ChildOnboardingSituation[]>([]);
  public situationItems = computed(() => {
    const currentSituations = this.situations();
    if (!currentSituations?.length) {
      return [];
    }

    return currentSituations.map((situation) => ({
      label: situation.name,
      value: situation.type,
    }));
  });

  @Output() public startOnboarding = new EventEmitter<UnderageOnboardingLegalGuardianshipStatus>();

  public readonly collapsed = signal<boolean>(true);
  public collapse = () => this.collapsed.update((collapsed) => !collapsed);

  public readonly InitialCheckBoxType = InitialCheckBoxType;
  public readonly UnderageOnboardingLegitimizationDocument = UnderageOnboardingLegitimizationDocument;

  private readonly initialChecklist: ChecklistState = {
    [UnderageOnboardingLegitimizationDocument.BIRTH_CERTIFICATE]: false,
    [InitialCheckBoxType.customerIdLegalGuardian2]: false,
    [InitialCheckBoxType.allGermanResidenceTax]: false,
    [InitialCheckBoxType.passport]: false,
  };
  public checklist = signal<ChecklistState>({ ...this.initialChecklist });

  public selectedSituation = signal<ChildOnboardingSituation | null>(null);
  public formValid = computed(() => {
    const situation = this.selectedSituation();
    if (!situation) {
      return false;
    }

    return Object.values(this.checklist()).every((value) => value === true);
  });

  public submit(): void {
    const situation = this.selectedSituation();
    if (this.formValid() && situation) {
      this.startOnboarding.emit(situation.type);
    }
  }

  public selectSituation(item: Item): void {
    const selected = this.situations().find((s) => s.type === item.value) ?? null;
    this.selectedSituation.set(selected);

    if (!selected) {
      this.checklist.set(this.initialChecklist);
      return;
    }

    const additionalDocumentChecklist: ChecklistState = {};
    (selected.documents ?? []).forEach((doc) => {
      if (doc.type !== UnderageOnboardingLegitimizationDocument.BIRTH_CERTIFICATE) {
        additionalDocumentChecklist[doc.type] = false;
      }
    });

    this.checklist.update((current) => {
      const preservedInitialValues = Object.keys(this.initialChecklist).reduce((acc, key) => {
        acc[key] = current[key] ?? this.initialChecklist[key];
        return acc;
      }, {} as ChecklistState);

      return {
        ...preservedInitialValues,
        ...additionalDocumentChecklist,
      };
    });
  }

  public toggleChecklistItem(item: string, value: boolean): void {
    this.checklist.update((current) => ({ ...current, [item]: value }));
  }
}
